import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search } from "lucide-react";

const AGE_GROUPS = ["mini", "junior", "senior", "adult"];

const ContestantsPage = () => {
  const [contestants, setContestants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAgeGroup, setFilterAgeGroup] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingContestant, setEditingContestant] = useState(null);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    age_group: "mini",
    is_member: false
  });

  useEffect(() => {
    fetchContestants();
  }, []);

  const fetchContestants = async () => {
    try {
      const response = await axios.get(`${API}/contestants`);
      setContestants(response.data);
    } catch (error) {
      toast.error("Failed to fetch contestants");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingContestant) {
        await axios.put(`${API}/contestants/${editingContestant.id}`, formData);
        toast.success("Contestant updated");
      } else {
        await axios.post(`${API}/contestants`, formData);
        toast.success("Contestant added");
      }
      fetchContestants();
      closeDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this contestant?")) return;
    try {
      await axios.delete(`${API}/contestants/${id}`);
      toast.success("Contestant deleted");
      fetchContestants();
    } catch (error) {
      toast.error("Failed to delete contestant");
    }
  };

  const openEditDialog = (contestant) => {
    setEditingContestant(contestant);
    setFormData({
      first_name: contestant.first_name,
      last_name: contestant.last_name,
      email: contestant.email || "",
      phone: contestant.phone || "",
      age_group: contestant.age_group,
      is_member: contestant.is_member
    });
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingContestant(null);
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      age_group: "mini",
      is_member: false
    });
  };

  const filteredContestants = contestants.filter((c) => {
    const matchesSearch = 
      c.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.email && c.email.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilter = filterAgeGroup === "all" || c.age_group === filterAgeGroup;
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contestants-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-text-primary">Contestants</h1>
          <p className="text-text-secondary mt-1">{contestants.length} total contestants</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground hover:bg-primary-hover" data-testid="add-contestant-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Contestant
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingContestant ? "Edit Contestant" : "Add Contestant"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contestant-form">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Name</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                    data-testid="contestant-firstname"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Name</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                    data-testid="contestant-lastname"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="contestant-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="contestant-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Age Group</Label>
                <Select
                  value={formData.age_group}
                  onValueChange={(value) => setFormData({ ...formData, age_group: value })}
                >
                  <SelectTrigger data-testid="contestant-agegroup">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AGE_GROUPS.map((ag) => (
                      <SelectItem key={ag} value={ag} className="capitalize">
                        {ag.charAt(0).toUpperCase() + ag.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_member"
                  checked={formData.is_member}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_member: checked })}
                  data-testid="contestant-member"
                />
                <Label htmlFor="is_member" className="cursor-pointer">Is a member</Label>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={closeDialog} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-primary text-primary-foreground hover:bg-primary-hover" data-testid="contestant-submit">
                  {editingContestant ? "Update" : "Add"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
          <Input
            placeholder="Search contestants..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-contestants"
          />
        </div>
        <Select value={filterAgeGroup} onValueChange={setFilterAgeGroup}>
          <SelectTrigger className="w-40" data-testid="filter-agegroup">
            <SelectValue placeholder="Age Group" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Ages</SelectItem>
            {AGE_GROUPS.map((ag) => (
              <SelectItem key={ag} value={ag} className="capitalize">
                {ag.charAt(0).toUpperCase() + ag.slice(1)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-primary/20 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/50">
              <TableHead className="font-heading font-bold text-text-primary">Name</TableHead>
              <TableHead className="font-heading font-bold text-text-primary">Email</TableHead>
              <TableHead className="font-heading font-bold text-text-primary">Phone</TableHead>
              <TableHead className="font-heading font-bold text-text-primary">Age Group</TableHead>
              <TableHead className="font-heading font-bold text-text-primary">Member</TableHead>
              <TableHead className="font-heading font-bold text-text-primary text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredContestants.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-text-secondary">
                  No contestants found
                </TableCell>
              </TableRow>
            ) : (
              filteredContestants.map((contestant) => (
                <TableRow key={contestant.id} className="hover:bg-surface-muted" data-testid={`contestant-row-${contestant.id}`}>
                  <TableCell className="font-medium text-text-primary">
                    {contestant.first_name} {contestant.last_name}
                  </TableCell>
                  <TableCell className="text-text-secondary">{contestant.email || "-"}</TableCell>
                  <TableCell className="text-text-secondary">{contestant.phone || "-"}</TableCell>
                  <TableCell>
                    <span className="px-2 py-1 bg-primary/20 text-primary-foreground rounded text-xs font-medium capitalize">
                      {contestant.age_group}
                    </span>
                  </TableCell>
                  <TableCell>
                    {contestant.is_member ? (
                      <span className="px-2 py-1 bg-status-success/20 text-status-success rounded text-xs font-medium">
                        Yes
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-100 text-text-muted rounded text-xs font-medium">
                        No
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditDialog(contestant)}
                        className="h-8 w-8 p-0"
                        data-testid={`edit-contestant-${contestant.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(contestant.id)}
                        className="h-8 w-8 p-0 text-status-error hover:text-status-error"
                        data-testid={`delete-contestant-${contestant.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ContestantsPage;
